import axios from "axios";
import React, { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MePaymentInstrumentService,
  PaymentCardType,
  PaymentInstrumentSummary,
} from "../../../api/mePaymentInstrument.service";
import {
  HailingStepData,
  PaymentMethod,
  hasMultipleVehicleClasses,
  selectedVehicleClasses,
} from "../mock/data";

export interface PaymentProps {
  state: HailingStepData;
  isSubmitting: boolean;
  orderError: boolean;
  fareChanged?: boolean;
  onStepComplete: (
    paymentInstrumentId: string,
    paymentMethod: PaymentMethod,
  ) => void;
  onBack: () => void;
}

type PaymentChoice = {
  id: string;
  name: string;
  mark: string;
  markBackground: string;
  markForeground: string;
  markBorder: string;
  detailKey?: "applePayDetail" | "googlePayDetail";
  nameKey?: "octopus" | "alipayHk" | "wechatPayHk" | "payMe";
  instructionKey?: "octopus" | "alipayHk" | "wechatPayHk" | "payMe";
  isHongKongWallet?: boolean;
};

const EXPRESS_CHECKOUT: PaymentChoice[] = [
  {
    id: "apple-pay",
    name: "Apple Pay",
    mark: "Apple Pay",
    markBackground: "#000000",
    markForeground: "#FFFFFF",
    markBorder: "#000000",
    detailKey: "applePayDetail",
  },
  {
    id: "google-pay",
    name: "Google Pay",
    mark: "G Pay",
    markBackground: "#FFFFFF",
    markForeground: "#3C4043",
    markBorder: "#E3E4E5",
    detailKey: "googlePayDetail",
  },
];

const HONG_KONG_WALLETS: PaymentChoice[] = [
  {
    id: "octopus",
    name: "Octopus",
    mark: "Octopus",
    markBackground: "#F39C1F",
    markForeground: "#FFFFFF",
    markBorder: "#F39C1F",
    nameKey: "octopus",
    instructionKey: "octopus",
    isHongKongWallet: true,
  },
  {
    id: "alipay-hk",
    name: "AlipayHK",
    mark: "AlipayHK",
    markBackground: "#1677FF",
    markForeground: "#FFFFFF",
    markBorder: "#1677FF",
    nameKey: "alipayHk",
    instructionKey: "alipayHk",
    isHongKongWallet: true,
  },
  {
    id: "wechat-pay-hk",
    name: "WeChat Pay HK",
    mark: "WeChat",
    markBackground: "#07C160",
    markForeground: "#FFFFFF",
    markBorder: "#07C160",
    nameKey: "wechatPayHk",
    instructionKey: "wechatPayHk",
    isHongKongWallet: true,
  },
  {
    id: "payme",
    name: "PayMe",
    mark: "PayMe",
    markBackground: "#DB0011",
    markForeground: "#FFFFFF",
    markBorder: "#DB0011",
    nameKey: "payMe",
    instructionKey: "payMe",
    isHongKongWallet: true,
  },
];

const HONG_KONG_WALLETS_ENABLED = false;
// Hidden for now: Apple Pay and Google Pay are not wired to order creation yet.
const EXPRESS_CHECKOUT_ENABLED = false;

const Payment: React.FC<PaymentProps> = ({
  state,
  isSubmitting,
  orderError,
  fareChanged = false,
  onStepComplete,
  onBack,
}) => {
  const { t } = useTranslation("hailing");
  const [selectedPaymentId, setSelectedPaymentId] = useState("");
  const [savedCards, setSavedCards] = useState<PaymentInstrumentSummary[]>([]);
  const [isLoadingSavedCards, setIsLoadingSavedCards] = useState(true);
  const [savedCardsError, setSavedCardsError] = useState("");
  const [showAddCardForm, setShowAddCardForm] = useState(false);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [addCardError, setAddCardError] = useState("");
  const [verificationUrl, setVerificationUrl] = useState("");
  const [cardForm, setCardForm] = useState({
    cardHolderName: "",
    cardNumber: "",
    cardType: "VISA" as PaymentCardType,
    expiryDate: "",
    cvv: "",
  });
  const [paymentInstrumentService] = useState(
    () => new MePaymentInstrumentService(),
  );

  React.useEffect(() => {
    let isActive = true;
    paymentInstrumentService
      .getSavedPaymentInstruments()
      .then((cards) => {
        if (isActive) setSavedCards(cards);
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setSavedCardsError(
          error instanceof Error
            ? error.message
            : t("steps.payment.loadSavedCardsError"),
        );
      })
      .finally(() => {
        if (isActive) setIsLoadingSavedCards(false);
      });

    return () => {
      isActive = false;
    };
  }, [paymentInstrumentService, t]);
  const isMeter = state.fareKind === "meter";
  const vehicle = state.vehicleClass;
  const fareKindLabel = isMeter
    ? t("steps.prices.meter")
    : t("steps.prices.flat");
  const isMultiple = hasMultipleVehicleClasses(state);
  // With several classes the fare shown and held is the highest of them.
  const vehicleNames = selectedVehicleClasses(state)
    .map((selected) => selected.name)
    .join(" / ");
  const fareValue = "$" + (isMeter ? vehicle.meter : vehicle.flat);
  const fareText = (isMeter ? `${t("steps.prices.est")} ` : "") + fareValue;
  const ctaPrice = isMultiple ? t("steps.prices.upTo", { amount: fareText }) : fareText;
  const rows: { label: string; value: string }[] = isMeter
    ? [
        {
          label: t("steps.payment.meterFare"),
          value: isMultiple ? t("steps.prices.upTo", { amount: vehicle.price }) : vehicle.price,
        },
        {
          label: t("steps.payment.tolls"),
          value: t("steps.payment.tollsAdded"),
        },
        { label: t("steps.payment.tempHold"), value: fareValue },
      ]
    : [
        {
          label: t("steps.payment.fixedFare"),
          value: isMultiple ? t("steps.prices.upTo", { amount: fareValue }) : fareValue,
        },
        {
          label: t("steps.payment.tolls"),
          value: t("steps.payment.tollsIncluded"),
        },
        {
          label: t("steps.payment.waiting"),
          value: t("steps.payment.waitingValue"),
        },
      ];

  const selectedHongKongWallet = HONG_KONG_WALLETS.find(
    (choice) => choice.id === selectedPaymentId,
  );
  const selectedCard = savedCards.find(
    (card) => card.id === selectedPaymentId,
  );

  const resetCardForm = () => {
    setCardForm({
      cardHolderName: "",
      cardNumber: "",
      cardType: "VISA",
      expiryDate: "",
      cvv: "",
    });
  };

  const handleAddCard = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const expiryDate = cardForm.expiryDate.match(
      /^(0[1-9]|1[0-2])\/(\d{2})$/,
    );
    if (!expiryDate?.[1] || !expiryDate[2]) {
      setAddCardError(t("steps.payment.invalidExpiryDate"));
      return;
    }

    setIsAddingCard(true);
    setAddCardError("");
    setVerificationUrl("");

    try {
      const result = await paymentInstrumentService.createPaymentInstrument({
        cardHolderName: cardForm.cardHolderName.trim(),
        cardNumber: cardForm.cardNumber.replace(/[\s-]/g, ""),
        cardType: cardForm.cardType,
        expirationMonth: expiryDate[1],
        expirationYear: `20${expiryDate[2]}`,
        securityCode: cardForm.cvv,
      });
      setSavedCards((cards) => [
        result.paymentInstrument,
        ...cards.filter((card) => card.id !== result.paymentInstrument.id),
      ]);
      setSelectedPaymentId(result.paymentInstrument.id);
      setVerificationUrl(result.webviewVerificationUrl ?? "");
      resetCardForm();
      setShowAddCardForm(false);
    } catch (error: unknown) {
      const responseMessage =
        axios.isAxiosError<{ message?: unknown }>(error) &&
        typeof error.response?.data?.message === "string"
          ? error.response.data.message
          : "";
      setCardForm((form) => ({ ...form, cvv: "" }));
      setAddCardError(
        responseMessage || t("steps.payment.addCardError"),
      );
    } finally {
      setIsAddingCard(false);
    }
  };

  const renderChoice = (choice: PaymentChoice, compact = false) => {
    const isSelected = selectedPaymentId === choice.id;
    const name = choice.nameKey
      ? t(`steps.payment.walletNames.${choice.nameKey}`)
      : choice.name;
    const detail = choice.detailKey
      ? t(`steps.payment.${choice.detailKey}`)
      : undefined;
    return (
      <label
        key={choice.id}
        className={`flex min-h-0 w-full cursor-pointer items-center gap-3 rounded-[14px] border-[1.5px] px-3 py-2.5 text-left transition-colors focus-within:ring-2 focus-within:ring-brand-blue focus-within:ring-offset-2 ${
          compact ? "gap-2.5 px-2.5" : ""
        } ${
          isSelected
            ? "border-brand-blue bg-[#EAF3FB]"
            : "border-transparent bg-white hover:border-[#C8D2DA]"
        }`}
      >
        <input
          type="radio"
          name="payment-method"
          value={choice.id}
          checked={isSelected}
          onChange={() => setSelectedPaymentId(choice.id)}
          className="sr-only"
        />
        <span
          aria-hidden="true"
          className={`flex h-[26px] w-10 flex-none items-center justify-center overflow-hidden rounded-[5px] border text-center font-bold ${
            compact ? "text-[7px]" : "text-[8px]"
          }`}
          style={{
            backgroundColor: choice.markBackground,
            color: choice.markForeground,
            borderColor: choice.markBorder,
          }}
        >
          {choice.mark}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-medium text-ink-100">
            {name}
          </span>
          {detail ? (
            <span className="block text-[11px] leading-[1.4] text-[#535A60]">
              {detail}
            </span>
          ) : null}
        </span>
        <span
          aria-hidden="true"
          className={`font-material-symbols text-[21px] leading-none ${
            isSelected
              ? "text-brand-blue [font-variation-settings:'FILL'_1]"
              : "text-[#B5B8BA]"
          }`}
        >
          {isSelected ? "radio_button_checked" : "radio_button_unchecked"}
        </span>
      </label>
    );
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          aria-label={t("steps.payment.back")}
          className="-ml-1 flex h-11 w-11 items-center justify-center rounded-full bg-white text-ink-100 transition-shadow hover:shadow-[0_4px_16px_rgba(15,21,26,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="font-material-symbols text-[18px] leading-none">
            arrow_back_ios_new
          </span>
        </button>
        <h1 className="text-[21px] font-bold tracking-[-0.4px] text-ink-100 sm:text-[22px] md:text-[24px]">
          {t("steps.payment.title")}
        </h1>
      </div>

      <div className="mt-4 flex flex-col gap-1.5 rounded-2xl bg-white px-4 py-3.5">
        <div className="mb-1 flex items-baseline justify-between gap-3">
          <span className="truncate text-[15px] font-semibold text-ink-100">
            {vehicleNames} · {fareKindLabel}
          </span>
          <span className="flex-none text-[19px] font-bold text-ink-100 md:text-[20px]">
            {ctaPrice}
          </span>
        </div>
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex justify-between gap-3 text-[13px]"
          >
            <span className="truncate text-[#3A4045]">{row.label}</span>
            <span className="flex-none font-medium text-ink-100">
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col">
        <section className="mt-4" aria-labelledby="saved-cards-heading">
          <h2
            id="saved-cards-heading"
            className="text-[12px] font-semibold text-[#6B7075]"
          >
            {t("steps.payment.savedCards")}
          </h2>
          {savedCards.length === 0 ? (
            isLoadingSavedCards ? (
              <p
                role="status"
                className="mt-2 px-3 text-[12px] leading-[1.45] text-[#535A60]"
              >
                {t("steps.payment.loadingSavedCards")}
              </p>
            ) : (
              <p className="mt-2 px-3 text-[12px] leading-[1.45] text-[#535A60]">
                {t("steps.payment.noSavedCards")}
              </p>
            )
          ) : (
            <div className="mt-1 flex flex-col gap-1.5">
              {savedCards.map((card) => {
                const isSelected = selectedPaymentId === card.id;
                const cardLabel =
                  card.cardNumber ??
                  `•••• ${card.cardSuffix ?? ""}`.trim();
                return (
                  <label
                    key={card.id}
                    className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-[14px] border-[1.5px] px-3 ${
                      isSelected
                        ? "border-brand-blue bg-[#EAF3FB]"
                        : "border-transparent bg-white hover:border-[#C8D2DA]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment-method"
                      value={card.id}
                      checked={isSelected}
                      onChange={() => setSelectedPaymentId(card.id)}
                      className="sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className="flex h-[26px] w-10 flex-none items-center justify-center rounded-[4px] border border-[#E3E4E5] bg-white text-[8px] font-bold text-[#1A1F71]"
                    >
                      {card.cardType === "MASTERCARD" ? "MC" : "VISA"}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-medium text-ink-100">
                        {cardLabel}
                      </span>
                      <span className="block text-[11px] leading-[1.4] text-[#535A60]">
                        {card.cardHolderName ?? ""}
                        {card.state && card.state !== "VERIFIED"
                          ? ` · ${t("steps.payment.cardVerificationRequired")}`
                          : ""}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={`font-material-symbols text-[21px] leading-none ${
                        isSelected
                          ? "text-brand-blue [font-variation-settings:'FILL'_1]"
                          : "text-[#B5B8BA]"
                      }`}
                    >
                      {isSelected
                        ? "radio_button_checked"
                        : "radio_button_unchecked"}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {savedCardsError ? (
            <p
              role="alert"
              className="mt-2 px-3 text-[12px] leading-[1.45] text-[#A12622]"
            >
              {savedCardsError}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (showAddCardForm) {
                resetCardForm();
              }
              setShowAddCardForm((show) => !show);
              setAddCardError("");
              setVerificationUrl("");
            }}
            aria-expanded={showAddCardForm}
            className="mt-1 flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] font-medium text-brand-blue transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
          >
            <span
              aria-hidden="true"
              className="font-material-symbols w-10 text-center text-[22px] leading-none"
            >
              add_card
            </span>
            {showAddCardForm
              ? t("steps.payment.cancelAddCard")
              : t("steps.payment.addCard")}
          </button>
          {showAddCardForm ? (
            <form
              onSubmit={handleAddCard}
              aria-busy={isAddingCard}
              className="mt-2 flex flex-col gap-3 rounded-xl bg-white p-3"
            >
              <label className="flex flex-col gap-1 text-[12px] font-medium text-[#3A4045]">
                {t("steps.payment.cardHolderName")}
                <input
                  autoComplete="cc-name"
                  required
                  value={cardForm.cardHolderName}
                  onChange={(event) =>
                    setCardForm((form) => ({
                      ...form,
                      cardHolderName: event.target.value,
                    }))
                  }
                  className="min-h-11 rounded-lg border border-[#D5D7D9] px-3 text-[14px] text-ink-100 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 flex flex-col gap-1 text-[12px] font-medium text-[#3A4045]">
                  {t("steps.payment.cardNumber")}
                  <input
                    autoComplete="cc-number"
                    inputMode="numeric"
                    maxLength={23}
                    required
                    value={cardForm.cardNumber}
                    onChange={(event) =>
                      setCardForm((form) => ({
                        ...form,
                        cardNumber: event.target.value.replace(/[^\d -]/g, ""),
                      }))
                    }
                    className="min-h-11 rounded-lg border border-[#D5D7D9] px-3 text-[14px] text-ink-100 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                  />
                </label>
                <label className="col-span-2 flex flex-col gap-1 text-[12px] font-medium text-[#3A4045]">
                  {t("steps.payment.cardType")}
                  <select
                    required
                    value={cardForm.cardType}
                    onChange={(event) =>
                      setCardForm((form) => ({
                        ...form,
                        cardType: event.target.value as PaymentCardType,
                      }))
                    }
                    className="min-h-11 rounded-lg border border-[#D5D7D9] bg-white px-3 text-[14px] text-ink-100 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                  >
                    <option value="VISA">
                      {t("steps.payment.cardTypes.visa")}
                    </option>
                    <option value="MASTERCARD">
                      {t("steps.payment.cardTypes.mastercard")}
                    </option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-[12px] font-medium text-[#3A4045]">
                  {t("steps.payment.expiryDate")}
                  <input
                    autoComplete="cc-exp"
                    inputMode="numeric"
                    maxLength={5}
                    pattern="(0[1-9]|1[0-2])/[0-9]{2}"
                    placeholder="MM/YY"
                    required
                    value={cardForm.expiryDate}
                    onChange={(event) => {
                      const digits = event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 4);
                      const expiryDate =
                        digits.length > 2
                          ? `${digits.slice(0, 2)}/${digits.slice(2)}`
                          : digits;
                      setCardForm((form) => ({ ...form, expiryDate }));
                    }}
                    className="min-h-11 rounded-lg border border-[#D5D7D9] px-3 text-[14px] text-ink-100 placeholder:text-[#8A8F93] focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                  />
                </label>
                <label className="flex flex-col gap-1 text-[12px] font-medium text-[#3A4045]">
                  {t("steps.payment.cvv")}
                  <input
                    autoComplete="cc-csc"
                    inputMode="numeric"
                    maxLength={3}
                    required
                    type="password"
                    value={cardForm.cvv}
                    onChange={(event) =>
                      setCardForm((form) => ({
                        ...form,
                        cvv: event.target.value.replace(/\D/g, "").slice(0, 3),
                      }))
                    }
                    className="min-h-11 rounded-lg border border-[#D5D7D9] px-3 text-[14px] text-ink-100 focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                  />
                </label>
              </div>
              {addCardError ? (
                <p role="alert" className="text-[12px] leading-[1.45] text-[#A12622]">
                  {addCardError}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={isAddingCard}
                className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand-blue px-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#0F66B4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isAddingCard ? (
                  <>
                    <span
                      aria-hidden="true"
                      className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    />
                    {t("steps.payment.addingCard")}
                  </>
                ) : (
                  t("steps.payment.saveCard")
                )}
              </button>
            </form>
          ) : null}
          {verificationUrl ? (
            <div
              role="status"
              className="mt-2 rounded-xl bg-[#FFF8E6] px-3 py-2.5 text-[12px] leading-[1.45] text-[#59430D]"
            >
              <p>{t("steps.payment.cardVerificationRequired")}</p>
              <a
                href={verificationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex min-h-11 items-center font-semibold text-brand-blue underline"
              >
                {t("steps.payment.continueVerification")}
              </a>
            </div>
          ) : null}
        </section>

        {EXPRESS_CHECKOUT_ENABLED ? (
          <section className="mt-3" aria-labelledby="express-checkout-heading">
            <h2
              id="express-checkout-heading"
              className="text-[12px] font-semibold text-[#6B7075]"
            >
              {t("steps.payment.expressCheckout")}
            </h2>
            <div className="mt-2 flex flex-col gap-1.5">
              {EXPRESS_CHECKOUT.map((choice) => renderChoice(choice))}
            </div>
          </section>
        ) : null}

        {HONG_KONG_WALLETS_ENABLED ? (
          <section className="mt-3" aria-labelledby="hk-wallets-heading">
            <h2
              id="hk-wallets-heading"
              className="text-[12px] font-semibold text-[#6B7075]"
            >
              {t("steps.payment.hongKongWallets")}
            </h2>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {HONG_KONG_WALLETS.map((choice) => renderChoice(choice, true))}
            </div>
            {selectedHongKongWallet?.isHongKongWallet ? (
              <p
                role="status"
                className="mt-2 flex items-start gap-2 rounded-xl bg-white px-3 py-2.5 text-[12px] leading-[1.45] text-[#3A4045]"
              >
                <span
                  aria-hidden="true"
                  className="font-material-symbols text-[18px] leading-[1.2] text-brand-blue"
                >
                  qr_code_scanner
                </span>
                <span>
                  {t(
                    `steps.payment.walletInstructions.${selectedHongKongWallet.instructionKey}`,
                  )}{" "}
                  {t("steps.payment.onYourPhoneToPay")}{" "}
                  <span className="sr-only">
                    {t("steps.payment.scanPreviewNote")}
                  </span>
                </span>
              </p>
            ) : null}
          </section>
        ) : null}
      </div>

      {orderError && (
        <p
          role="alert"
          className="mt-3 rounded-xl bg-[#FFF1F0] px-3 py-2.5 text-[13px] leading-[1.45] text-[#A12622]"
        >
          {t("steps.payment.orderError")}
        </p>
      )}

      {fareChanged && (
        <p
          role="status"
          className="mt-3 rounded-xl bg-[#FFF8E6] px-3 py-2.5 text-[13px] leading-[1.45] text-[#59430D]"
        >
          {t("steps.payment.fareChanged", { price: ctaPrice })}
        </p>
      )}

      <div className="mt-auto flex flex-col gap-2 pt-5">
        <p className="text-[11px] leading-[1.45] text-[#535A60]">
          {isMeter
            ? t("steps.payment.holdNoteMeter")
            : t("steps.payment.holdNoteFlat")}
        </p>
        <button
          type="button"
          onClick={() =>
            selectedCard &&
            onStepComplete(selectedCard.id, {
              id: selectedCard.id,
              name:
                selectedCard.cardType === "MASTERCARD"
                  ? "Mastercard"
                  : "Visa",
              sub: selectedCard.cardSuffix
                ? `•••• ${selectedCard.cardSuffix}`
                : "",
              type: "card",
            })
          }
          disabled={isSubmitting || !selectedCard}
          className="flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-brand-blue px-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#0F66B4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:px-5 sm:text-[16px] md:text-[17px]"
        >
          {isSubmitting && (
            <span
              aria-hidden="true"
              className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white"
            />
          )}
          {isSubmitting
            ? t("steps.payment.submitting")
            : t("steps.payment.confirmRide", { price: ctaPrice })}
        </button>
      </div>
    </div>
  );
};

export default Payment;
